import * as React from 'react';

import { ItemAliasRecord } from '@rematter/modules/settings/types/inventoryItem';
import { Badge, DataGridColumn, LocalizedDate } from '@rematter/ui-web';

import { useTranslations } from '../../../../i18n/useTranslations';

type Props = {
  onRowClick?: (row: ItemAliasRecord) => void;
};

export const useItemAliasesPanelColumns = ({ onRowClick }: Props = {}) => {
  const t = useTranslations();

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: ItemAliasRecord }) => {
      onRowClick?.(row);
    },
    [onRowClick],
  );

  const columns: DataGridColumn<ItemAliasRecord>[] = React.useMemo(
    () => [
      {
        key: 'item',
        header: t.inventoryView.itemAliasesPanel.columns.item,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left font-medium">
            {row.item.name}
          </div>
        ),
      },
      {
        key: 'aliasName',
        header: t.inventoryView.itemAliasesPanel.columns.aliasName,
        sortable: true,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <div className="text-left">{row.aliasName}</div>,
      },
      {
        key: 'aliasCode',
        header: t.inventoryView.itemAliasesPanel.columns.aliasCode,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left font-mono">{row.aliasCode ?? '-'}</div>
        ),
      },
      {
        key: 'account',
        header: t.inventoryView.itemAliasesPanel.columns.account,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <div className="text-left">{row.account.name}</div>,
      },
      {
        key: 'aliasType',
        header: t.inventoryView.itemAliasesPanel.columns.type,
        sortable: true,
        width: 80,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge
            color={row.aliasType === 'Buy' ? 'warning' : 'success'}
            size="small"
          >
            {row.aliasType}
          </Badge>
        ),
      },
      {
        key: 'createdAt',
        header: t.inventoryView.itemAliasesPanel.columns.created,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
